import { ApolloClient, InMemoryCache, ApolloLink, Observable, gql } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import profile from './profile';
import {useAuthStore} from "./store/useAuthStore";
import {getAccessKey, getRefreshKey, setAuthKey} from "./util/secureAuthStorage";
import { getApiEnv } from "./util/getApiEnv";

const QUERY_REFRESH_TOKEN = gql`
  query RefreshToken($refreshToken: String!) {
    refreshToken(refreshToken: $refreshToken) {
      access_token
      refresh_token
    }
  }
`;

async function refreshAccessToken(refreshToken) {
    const { data } = await apolloClient.mutate({
        mutation: QUERY_REFRESH_TOKEN,
        variables: { refreshToken },
    });

    return data;
}

const uploadLink = createUploadLink({
    uri: profile.apiHost(getApiEnv()),
});

const authLink = setContext((_, { headers }) => {
    const token = getAccessKey();

    return {
        headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
        }
    }
});

const errorLink = onError(
    ({ graphQLErrors, operation, forward }) => {
        console.log(graphQLErrors);
        if (graphQLErrors) {
            for (let err of graphQLErrors) {
                console.log(err);
                switch (err.statusCode) {
                    case 403:
                        if (operation.operationName === 'refreshToken') return;

                        // eslint-disable-next-line no-case-declarations
                        const observable = new Observable(
                            (observer) => {
                                (async () => {
                                    try {
                                        if (getRefreshKey() === null) {
                                            throw new Error('refresh_token is null');
                                        }

                                        const data = await refreshAccessToken(getRefreshKey());

                                        if (data) {
                                            setAuthKey(data.refreshToken.access_token, data.refreshToken.refresh_token);
                                        }
                                        const subscriber = {
                                            next: observer.next.bind(observer),
                                            error: observer.error.bind(observer),
                                            complete: observer.complete.bind(observer),
                                        };

                                        forward(operation).subscribe(subscriber);
                                    } catch (err) {
                                        observer.error(err);
                                        useAuthStore.getState().logout();
                                    }
                                })();
                            }
                        );
                        return observable;
                }
            }
        }
    }
);


export const apolloClient = new ApolloClient({
    cache: new InMemoryCache({
        typePolicies: {
            Query: {
                fields: {
                    seeProduct: {
                        read(_, { args, toReference }) {
                            if (!args?.id) return _;
                            return toReference({ __typename: 'ProductNotMine', id: String(args.id) });
                        },
                    },
                    seeProductResponse: {
                        merge(existing = {}, incoming) {
                            return { ...existing, ...incoming };
                        },
                    },
                    getPopularKeywordsByFilterCached: {
                        merge(existing = {}, incoming) {
                            // 기존 데이터와 새로운 데이터를 병합
                            return { ...existing, ...incoming };
                        },
                    },
                },
            },
        },
    }).restore((() => {
        if (typeof document === 'undefined') return {};
        try {
            return JSON.parse(document.getElementById('__APOLLO_STATE__')?.textContent || '{}');
        } catch (e) {
            console.warn('[Apollo] Failed to parse SSR state:', e);
            return {};
        }
    })()),
    link: ApolloLink.from([errorLink, authLink, uploadLink]),
    ssrForceFetchDelay: 100,
});
